<template>
  <div class="automated-assessment-report-page">
    <div class="report-page-header">
      <h2 class="assessment-title">Credit Assessment Report</h2>
    </div>

    <div class="main-content">
      <div v-if="loading" class="loading-state">
        <BaseSpinner />
      </div>

      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button class="retry-btn" @click="loadInitialData">Retry</button>
      </div>

      <div v-else-if="!creditModelDoc" class="empty-state">
        <i class="las la-file-alt empty-state-icon"></i>
        <p>No credit model has been run yet.</p>
        <p class="empty-state-hint">Go to the <strong>Model</strong> tab to run the automated credit model first.</p>
      </div>

      <div v-else class="report-content">
        <!-- Approved Credit Fields -->
        <div class="approved-credit-fields">
          <div class="approved-credit-field">
            <label class="form-label">Approved Risk Based Pricing (%)</label>
            <div class="rate-display-card">
              <template v-if="approvedRateDisplay">
                <span class="rate-display-value">{{ approvedRateDisplay }}</span>
              </template>
              <template v-else>
                <span class="rate-display-placeholder">Set approved rate in organisation details</span>
              </template>
            </div>
          </div>
          <div class="approved-credit-field">
            <label class="form-label">Approved Advance Rate (%)</label>
            <input
              v-model="approvedAdvanceRate"
              type="number"
              class="form-input"
              placeholder="Enter percentage"
              step="0.01"
              min="0"
              max="100"
            />
          </div>
          <BaseButton
            type="button"
            stylevariant="primary"
            :cus_style="{ width: 'auto', padding: '0.5rem 0.875rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', alignSelf: 'flex-end' }"
            :disabled="savingApprovedCreditFields"
            :loading="savingApprovedCreditFields"
            @click="saveApprovedCreditFields"
          >
            Save
          </BaseButton>
        </div>

        <!-- Prime Payers Import -->
        <div v-if="!hasImportedPrimePayers" class="prime-payers-import">
          <label class="subsection-title">Import Prime Payers</label>
          <p class="prime-payers-import-hint">
            Upload an Excel file to check matches against the catalog. Review and resolve each payer, then confirm the import.
          </p>

          <!-- File + check row -->
          <div class="prime-payers-import-row">
            <div class="prime-payers-file-input-container">
              <input
                ref="primePayersFileInput"
                type="file"
                class="prime-payers-file-input"
                accept=".xlsx,.xls,.csv"
                @change="onPrimePayersFileChange"
              />
              <div class="prime-payers-file-input-overlay">
                <template v-if="primePayersImportFile">
                  <i class="las la-file-excel prime-payers-file-icon"></i>
                  <span class="prime-payers-file-selected">{{ primePayersImportFile.name }}</span>
                </template>
                <template v-else>
                  <i class="las la-cloud-upload-alt prime-payers-file-icon"></i>
                  <span class="prime-payers-file-hint">Choose .xlsx, .xls, or .csv file</span>
                </template>
              </div>
            </div>
            <BaseButton
              type="button"
              stylevariant="secondary"
              :cus_style="{ width: 'auto', padding: '0.5rem 0.875rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
              :disabled="!primePayersImportFile || primePayersChecking || primePayersImporting"
              :loading="primePayersChecking"
              @click="checkPrimePayers"
            >
              <i v-if="!primePayersChecking" class="las la-search"></i>
              Check Matches
            </BaseButton>
          </div>

          <!-- Error -->
          <div v-if="primePayersCheckError" class="prime-payers-check-error">
            <i class="las la-exclamation-triangle"></i>
            <span>{{ primePayersCheckError }}</span>
          </div>

          <!-- Checking state -->
          <div v-if="primePayersChecking" class="prime-payers-check-loading">
            <BaseSpinner size="small" />
            <span>Checking Excel against prime payers catalog…</span>
          </div>

          <!-- ── Resolution table ── -->
          <div v-if="primePayersCheckResult && !primePayersChecking" class="prc-panel">

            <!-- Summary + filter row -->
            <div class="prc-header">
              <p class="prc-summary-text">
                <strong>{{ primePayersCheckResult.matched_count }}</strong> matched in catalog ·
                <strong>{{ primePayersCheckResult.unmatched_count }}</strong> new ·
                <strong>{{ primePayersCheckResult.total }}</strong> total
              </p>
              <div class="prc-filter-tabs">
                <button type="button" class="prc-tab" :class="{ active: primePayersCheckFilter === 'all' }" @click="primePayersCheckFilter = 'all'">All</button>
                <button type="button" class="prc-tab" :class="{ active: primePayersCheckFilter === 'matched' }" @click="primePayersCheckFilter = 'matched'">Matched</button>
                <button type="button" class="prc-tab" :class="{ active: primePayersCheckFilter === 'unmatched' }" @click="primePayersCheckFilter = 'unmatched'">New</button>
              </div>
            </div>

            <!-- List -->
            <div class="prc-list">
              <!-- Column headers -->
              <div class="prc-row prc-row--head">
                <span class="prc-col-name">Payer (from Excel)</span>
                <span class="prc-col-status">Status</span>
                <span class="prc-col-resolution">Import as</span>
              </div>

              <!-- Data rows -->
              <div
                v-for="({ row, globalIndex }) in filteredPrimePayersCheckRows"
                :key="`prc-${globalIndex}`"
                class="prc-row"
              >
                <!-- Name + metadata -->
                <div class="prc-col-name">
                  <div class="prc-name">{{ formatPrimePayerValue(row.name) }}</div>
                  <div class="prc-meta">
                    <span v-if="row.parent">{{ formatPrimePayerValue(row.parent) }}</span>
                    <span v-if="row.sector">{{ formatPrimePayerValue(row.sector) }}</span>
                    <span v-if="row.moody_rating">{{ formatPrimePayerValue(row.moody_rating) }}</span>
                  </div>
                </div>

                <!-- Status badge -->
                <div class="prc-col-status">
                  <span class="prc-status-badge" :class="row.has_match ? 'matched' : 'new'">
                    {{ row.has_match ? 'Matched' : 'New' }}
                  </span>
                </div>

                <!-- Resolution -->
                <div class="prc-col-resolution">
                  <ChatbotSelect
                    v-if="row.has_match && row.matching_prime_payers.length"
                    :value="primePayersResolutions[globalIndex] || 'keep'"
                    :options="getResolutionOptions(row)"
                    :reduce="o => o.value"
                    placeholder="Choose…"
                    @input="setResolution(globalIndex, $event)"
                  />
                  <span v-else class="prc-new-text">Added as-is</span>
                </div>
              </div>

              <div v-if="!filteredPrimePayersCheckRows.length" class="prc-empty-cell">
                No payers for this filter.
              </div>
            </div>

            <!-- Confirm action -->
            <div class="prc-actions">
              <span class="prc-actions-note">{{ resolvedPrimePayersList.length }} payer{{ resolvedPrimePayersList.length !== 1 ? 's' : '' }} will be imported</span>
              <BaseButton
                type="button"
                stylevariant="primary"
                :cus_style="{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
                :disabled="primePayersImporting || resolvedPrimePayersList.length === 0"
                :loading="primePayersImporting"
                @click="importResolvedPrimePayers"
              >
                <i v-if="!primePayersImporting" class="las la-cloud-upload-alt"></i>
                Confirm &amp; Import
              </BaseButton>
            </div>
          </div>
        </div>

        <!-- Imported Prime Payers Table -->
        <div v-if="hasImportedPrimePayers" class="prime-payers-section">
          <div class="prime-payers-header">
            <h3 class="subsection-title">Prime Payers</h3>
            <BaseButton
              type="button"
              stylevariant="primary"
              :cus_style="{ width: 'auto', padding: '0.5rem 0.875rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
              :disabled="primePayersDeleting"
              :loading="primePayersDeleting"
              @click="deleteImportedPrimePayers"
            >
              <i class="las la-trash"></i>
              Delete Import
            </BaseButton>
          </div>
          <div class="prime-payers-table-wrapper">
            <table class="prime-payers-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Parent</th>
                  <th>Sector</th>
                  <th>Moody's Rating</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(payer, index) in primePayers"
                  :key="`${payer.name || 'payer'}-${index}`"
                >
                  <td>{{ formatPrimePayerValue(payer.name) }}</td>
                  <td>{{ formatPrimePayerValue(payer.parent) }}</td>
                  <td>{{ formatPrimePayerValue(payer.sector) }}</td>
                  <td>{{ formatPrimePayerValue(payer.moody_rating) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Analysis Progress -->
        <div
          v-if="showAssessmentProgress && !creditModelHasAnalysisData"
          class="assessment-progress-section"
        >
          <h3 class="subsection-title">Analysis Progress</h3>
          <div class="assessment-progress-container">
            <div class="assessment-progress-status" :class="assessmentProgressStatusClass">
              <i :class="getAssessmentStatusIcon(assessmentProgressStatus)"></i>
              <span>{{ assessmentProgressStatusLabel }}</span>
            </div>

            <div v-if="assessmentProgressMessage" class="assessment-progress-message">
              {{ assessmentProgressMessage }}
            </div>

            <div
              v-if="assessmentProgressValue !== null && assessmentProgressValue !== undefined"
              class="assessment-progress-bar-row"
            >
              <div class="assessment-progress-bar">
                <div
                  class="assessment-progress-bar-fill"
                  :style="{ width: `${assessmentProgressValue}%` }"
                ></div>
              </div>
              <span class="assessment-progress-percentage">{{ assessmentProgressValue }}%</span>
            </div>

            <div v-if="assessmentError" class="assessment-progress-error">
              <i class="las la-exclamation-triangle"></i>
              <span>{{ assessmentError }}</span>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="credit-assessment-report-actions">
          <p
            v-if="!creditModelHasAnalysisData && !hasApprovedCreditFields"
            class="analyse-prereq-hint"
          >
            Approved rate, approved advance rate, and prime payers are required before analysing.
          </p>
          <BaseButton
            v-if="!creditModelHasAnalysisData"
            type="button"
            stylevariant="primary"
            :cus_style="{ width: 'auto', padding: '0.5rem 0.875rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
            :disabled="!canAnalyseModelResult"
            :loading="generatingCreditAssessmentReport"
            @click="generateCreditAssessmentReport"
          >
            Analyse Model Result
          </BaseButton>
          <template v-else>
            <BaseButton
              type="button"
              stylevariant="primary"
              :cus_style="{ width: 'auto', padding: '0.5rem 0.875rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
              :disabled="!canGoToModelReport"
              @click="goToCreditModelReport"
            >
              Generate Model Report
            </BaseButton>
            <BaseButton
              type="button"
              stylevariant="primary"
              :cus_style="{ width: 'auto', padding: '0.5rem 0.875rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
              @click="goToPayerReport"
            >
              Generate Payer Report
            </BaseButton>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service';
import BaseButton from '@/components/UI/BaseButton.vue';
import BaseSpinner from '@/components/UI/BaseSpinner.vue';
import ChatbotSelect from './components/ChatbotSelect.vue';

const ASSESSMENT_STEP_LABELS = {
  payer_scores: 'Formatting payer scores',
  key_strengths: 'Analysing key strengths',
  key_weaknesses: 'Analysing key weaknesses',
  revenue_trends: 'Analysing revenue trends',
  cash_quick_ratio: 'Analysing cash and quick ratio',
  relative_risk_strengths: 'Analysing relative risk strengths',
  relative_risk_weaknesses: 'Analysing relative risk weaknesses',
  conclusion_points: 'Generating conclusion points',
  recommendation_points: 'Generating recommendations',
  completed: 'Analysis completed'
};

export default {
  name: 'AutomatedAssessmentReport',
  components: { BaseButton, BaseSpinner, ChatbotSelect },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      organisationDetails: {},
      creditModelDoc: null,
      creditModelHasAnalysisData: false,
      primePayersImportFile: null,
      primePayersImporting: false,
      primePayersDeleting: false,
      primePayersChecking: false,
      primePayersCheckResult: null,
      primePayersCheckError: null,
      primePayersCheckFilter: 'all',
      primePayersResolutions: {},
      approvedAdvanceRate: '',
      savingApprovedCreditFields: false,
      generatingCreditAssessmentReport: false,
      assessmentWebsocket: null,
      assessmentWebSocketReady: false,
      assessmentProgress: null,
      assessmentError: null
    };
  },
  computed: {
    creditModelFileId() {
      return this.creditModelDoc?.id || this.creditModelDoc?.file_id || null;
    },
    approvedRate() {
      const rate = this.organisationDetails?.approved_rate ?? this.organisationDetails?.approvedRate;
      return rate === '' || rate === null || rate === undefined ? null : rate;
    },
    approvedRateDisplay() {
      if (this.approvedRate === null) return null;
      const numeric = Number(this.approvedRate);
      if (!Number.isFinite(numeric)) return String(this.approvedRate);
      return `${numeric}%`;
    },
    primePayers() {
      const payers = this.organisationDetails.prime_payers
        ?? this.organisationDetails.primePayers
        ?? [];
      if (!Array.isArray(payers)) return [];
      return payers.map((payer) => ({
        name: payer.name ?? payer.Name ?? '',
        parent: payer.parent ?? payer.Parent ?? '',
        sector: payer.sector ?? payer.Sector ?? '',
        moody_rating: payer.moody_rating ?? payer.moodyRating ?? payer['Moody Rating'] ?? ''
      }));
    },
    hasImportedPrimePayers() {
      return this.primePayers.length > 0;
    },
    hasApprovedCreditFields() {
      const advanceRate = this.approvedAdvanceRate;
      return this.approvedRate !== null
        && advanceRate !== '' && advanceRate !== null && advanceRate !== undefined
        && this.hasImportedPrimePayers;
    },
    canAnalyseModelResult() {
      return !!this.creditModelFileId
        && !this.creditModelHasAnalysisData
        && !this.generatingCreditAssessmentReport
        && this.hasApprovedCreditFields;
    },
    canGoToModelReport() {
      return !!this.creditModelFileId && this.creditModelHasAnalysisData;
    },
    filteredPrimePayersCheckRows() {
      const rows = Array.isArray(this.primePayersCheckResult?.prime_payers)
        ? this.primePayersCheckResult.prime_payers
        : [];
      const withIndex = rows.map((row, globalIndex) => ({ row, globalIndex }));
      if (this.primePayersCheckFilter === 'matched') return withIndex.filter(({ row }) => row.has_match);
      if (this.primePayersCheckFilter === 'unmatched') return withIndex.filter(({ row }) => !row.has_match);
      return withIndex;
    },
    resolvedPrimePayersList() {
      const rows = Array.isArray(this.primePayersCheckResult?.prime_payers)
        ? this.primePayersCheckResult.prime_payers
        : [];
      return rows.map((row, i) => {
        const resolution = this.primePayersResolutions[i] ?? 'keep';

        // No catalog match, or user chose to keep the original Excel row → new payer
        if (!row.has_match || resolution === 'keep') {
          return {
            type: row.has_match ? 'original' : 'new',
            name: row.name ?? null,
            parent: row.parent ?? null,
            sector: row.sector ?? null,
            moody_rating: row.moody_rating ?? null
          };
        }

        // User picked a catalog match
        const matchIdx = parseInt(resolution.split(':')[1], 10);
        const match = row.matching_prime_payers[matchIdx];
        if (!match) {
          return {
            type: 'original',
            name: row.name ?? null,
            parent: row.parent ?? null,
            sector: row.sector ?? null,
            moody_rating: row.moody_rating ?? null
          };
        }
        return {
          type: 'matched',
          catalog_id: match.id ?? null,
          name: match.name ?? null,
          parent: match.parent ?? null,
          sector: match.sector ?? null,
          moody_rating: match.moody_rating ?? null
        };
      });
    },
    showAssessmentProgress() {
      return this.generatingCreditAssessmentReport
        || !!this.assessmentProgress
        || !!this.assessmentError;
    },
    assessmentProgressStatus() {
      return this.assessmentProgress?.status
        || (this.generatingCreditAssessmentReport ? 'processing' : null);
    },
    assessmentProgressStatusClass() {
      const status = this.assessmentProgressStatus;
      if (status === 'completed') return 'completed';
      if (status === 'failed') return 'failed';
      return 'processing';
    },
    assessmentProgressStatusLabel() {
      const status = this.assessmentProgressStatus;
      if (status === 'completed') return 'Completed';
      if (status === 'failed') return 'Failed';
      return 'Processing';
    },
    assessmentProgressMessage() {
      return this.assessmentProgress?.message || null;
    },
    assessmentProgressValue() {
      if (this.assessmentProgress?.progress === undefined
        || this.assessmentProgress?.progress === null) {
        return this.generatingCreditAssessmentReport ? 0 : null;
      }
      return this.assessmentProgress.progress;
    }
  },
  async mounted() {
    await this.loadInitialData();
  },
  beforeDestroy() {
    this.disconnectAssessmentWebSocket();
  },
  methods: {
    async loadInitialData() {
      if (!this.candidateId) {
        this.error = 'Candidate ID is required';
        return;
      }

      this.loading = true;
      this.error = null;
      this.organisationDetails = {};
      this.creditModelDoc = null;
      this.creditModelHasAnalysisData = false;
      this.clearPrimePayersFileInput();

      const toList = (res) => Array.isArray(res)
        ? res
        : (res?.data || res?.files || res?.uploads || []);

      try {
        const [details, creditModelResponse] = await Promise.all([
          ChatbotService.getOrganisationDetails(this.candidateId),
          ChatbotService.getUploads(this.candidateId, {
            type: 'credit_model',
            page: 1,
            page_size: 10
          })
        ]);

        this.organisationDetails = details || {};
        this.populateApprovedCreditFields(this.organisationDetails);

        const creditModelDocuments = toList(creditModelResponse);
        const sorted = [...creditModelDocuments].sort(
          (a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0)
        );
        this.creditModelDoc = sorted[0] || null;

        await this.loadCreditModelAnalysisState();
      } catch (err) {
        console.error('Error loading report data:', err);
        this.error = err.response?.data?.message || err.message || 'Failed to load report data.';
      } finally {
        this.loading = false;
      }
    },

    async loadCreditModelAnalysisState() {
      this.creditModelHasAnalysisData = false;

      if (!this.creditModelFileId) return;

      try {
        const fileDetails = await ChatbotService.getFileDetails(this.creditModelFileId);
        const transaction = fileDetails?.transactions?.[0];
        this.creditModelHasAnalysisData = this.hasCreditModelAnalysisData(transaction);
      } catch (err) {
        console.error('Error loading credit model analysis state:', err);
        this.creditModelHasAnalysisData = false;
      }
    },

    hasCreditModelAnalysisData(transaction) {
      const analysisData = transaction?.analysis_data || transaction?.analysisData;
      if (!analysisData || typeof analysisData !== 'object') {
        return false;
      }

      const sectionKeys = [
        'key_strengths',
        'key_weaknesses',
        'revenue_trends',
        'cash_quick_ratio',
        'relative_risk_strengths',
        'relative_risk_weaknesses',
        'conclusion_points',
        'recommendation_points',
        'payer_scores',
        'credit_recommendation'
      ];

      return sectionKeys.some((key) => {
        const value = analysisData[key];
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== undefined && value !== null && value !== '';
      });
    },

    populateApprovedCreditFields(details) {
      const source = details || {};
      const advanceRate = source.approved_advance_rate ?? source.approvedAdvanceRate;
      this.approvedAdvanceRate = advanceRate !== undefined && advanceRate !== null && advanceRate !== ''
        ? String(advanceRate)
        : '';
    },

    async saveApprovedCreditFields() {
      if (!this.candidateId || this.savingApprovedCreditFields) return;

      this.savingApprovedCreditFields = true;

      try {
        const payload = { ...this.organisationDetails };
        if (this.approvedAdvanceRate !== '' && this.approvedAdvanceRate !== null) {
          payload.approved_advance_rate = Number(this.approvedAdvanceRate);
        } else {
          delete payload.approved_advance_rate;
        }

        const saved = await ChatbotService.updateOrganisationDetails(this.candidateId, payload);
        this.organisationDetails = { ...(saved || payload) };
        this.populateApprovedCreditFields(this.organisationDetails);

        this.$bvToast.toast('Credit assessment fields saved.', {
          title: 'Success', variant: 'success', solid: true, autoHideDelay: 3000
        });
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.message || 'Failed to save credit assessment fields. Please try again.',
          { title: 'Error', variant: 'danger', solid: true, autoHideDelay: 5000 }
        );
      } finally {
        this.savingApprovedCreditFields = false;
      }
    },

    formatPrimePayerValue(value) {
      if (value === undefined || value === null || value === '') return '—';
      return String(value);
    },

    formatMatchScore(score) {
      const numeric = Number(score);
      if (!Number.isFinite(numeric)) return '—';
      return `${Math.round(numeric * 100)}%`;
    },

    resetPrimePayersCheckState() {
      this.primePayersCheckResult = null;
      this.primePayersCheckError = null;
      this.primePayersCheckFilter = 'all';
      this.primePayersResolutions = {};
    },

    setResolution(globalIndex, value) {
      this.$set(this.primePayersResolutions, globalIndex, value);
    },

    getResolutionOptions(row) {
      const opts = [
        { label: `Keep original — ${this.formatPrimePayerValue(row.name)}`, value: 'keep' }
      ];
      (row.matching_prime_payers || []).forEach((m, i) => {
        opts.push({
          label: `${this.formatPrimePayerValue(m.name)} (${this.formatMatchScore(m.match_score)})`,
          value: `match:${i}`
        });
      });
      return opts;
    },

    onPrimePayersFileChange(event) {
      const file = event.target.files?.[0];
      this.primePayersImportFile = file || null;
      this.resetPrimePayersCheckState();
    },

    clearPrimePayersFileInput() {
      this.primePayersImportFile = null;
      this.resetPrimePayersCheckState();
      if (this.$refs.primePayersFileInput) {
        this.$refs.primePayersFileInput.value = '';
      }
    },

    async checkPrimePayers() {
      if (!this.candidateId || !this.primePayersImportFile || this.primePayersChecking) return;

      this.primePayersChecking = true;
      this.primePayersCheckError = null;
      this.primePayersCheckResult = null;

      try {
        const result = await ChatbotService.checkPrimePayers(this.candidateId, this.primePayersImportFile);

        const rows = Array.isArray(result?.prime_payers)
          ? result.prime_payers.map((row) => ({
            name: row?.name ?? null,
            parent: row?.parent ?? null,
            sector: row?.sector ?? null,
            moody_rating: row?.moody_rating ?? null,
            ContactID: row?.ContactID ?? null,
            has_match: Boolean(row?.has_match),
            matching_prime_payers: Array.isArray(row?.matching_prime_payers)
              ? row.matching_prime_payers
              : []
          }))
          : [];

        this.primePayersCheckResult = {
          prime_payers: rows,
          total: Number.isFinite(Number(result?.total)) ? Number(result.total) : rows.length,
          matched_count: Number.isFinite(Number(result?.matched_count))
            ? Number(result.matched_count)
            : rows.filter((r) => r.has_match).length,
          unmatched_count: Number.isFinite(Number(result?.unmatched_count))
            ? Number(result.unmatched_count)
            : rows.filter((r) => !r.has_match).length
        };
        this.primePayersCheckFilter = 'all';
        // Default all resolutions to 'keep'
        const resolutions = {};
        rows.forEach((_, i) => { resolutions[i] = 'keep'; });
        this.primePayersResolutions = resolutions;
      } catch (error) {
        this.primePayersCheckError = error.response?.data?.message
          || 'Failed to check prime payers Excel. Please try again.';
        this.$bvToast.toast(this.primePayersCheckError, {
          title: 'Error', variant: 'danger', solid: true, autoHideDelay: 5000
        });
      } finally {
        this.primePayersChecking = false;
      }
    },

    async importPrimePayers() {
      if (!this.candidateId || !this.primePayersImportFile || this.primePayersImporting) return;

      this.primePayersImporting = true;

      try {
        const result = await ChatbotService.importPrimePayers(this.candidateId, this.primePayersImportFile);
        const details = await ChatbotService.getOrganisationDetails(this.candidateId);
        const importedPayers = Array.isArray(result?.prime_payers)
          ? result.prime_payers
          : (Array.isArray(details?.prime_payers) ? details.prime_payers : []);

        this.organisationDetails = { ...(details || {}), prime_payers: importedPayers };

        this.$bvToast.toast('Prime payers imported successfully.', {
          title: 'Success', variant: 'success', solid: true, autoHideDelay: 3000
        });

        this.clearPrimePayersFileInput();
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.message || 'Failed to import prime payers. Please try again.',
          { title: 'Error', variant: 'danger', solid: true, autoHideDelay: 5000 }
        );
      } finally {
        this.primePayersImporting = false;
      }
    },

    async importResolvedPrimePayers() {
      if (!this.candidateId || this.primePayersImporting || !this.resolvedPrimePayersList.length) return;

      this.primePayersImporting = true;

      try {
        const result = await ChatbotService.importPrimePayersJson(this.candidateId, this.resolvedPrimePayersList);
        const details = await ChatbotService.getOrganisationDetails(this.candidateId);
        const importedPayers = Array.isArray(result?.prime_payers)
          ? result.prime_payers
          : (Array.isArray(details?.prime_payers) ? details.prime_payers : this.resolvedPrimePayersList);

        this.organisationDetails = { ...(details || {}), prime_payers: importedPayers };

        this.$bvToast.toast(`${this.resolvedPrimePayersList.length} prime payer${this.resolvedPrimePayersList.length !== 1 ? 's' : ''} imported successfully.`, {
          title: 'Success', variant: 'success', solid: true, autoHideDelay: 3000
        });

        this.clearPrimePayersFileInput();
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.message || 'Failed to import prime payers. Please try again.',
          { title: 'Error', variant: 'danger', solid: true, autoHideDelay: 5000 }
        );
      } finally {
        this.primePayersImporting = false;
      }
    },

    async deleteImportedPrimePayers() {
      if (!this.candidateId || !this.hasImportedPrimePayers || this.primePayersDeleting) return;

      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to delete the imported prime payers? This action cannot be undone.',
        {
          title: 'Confirm Delete',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      );

      if (!confirmed) return;

      this.primePayersDeleting = true;

      try {
        const saved = await ChatbotService.updateOrganisationDetails(this.candidateId, {
          prime_payers: []
        });

        this.organisationDetails = { ...(saved || this.organisationDetails), prime_payers: [] };
        if (Object.prototype.hasOwnProperty.call(this.organisationDetails, 'primePayers')) {
          this.$delete(this.organisationDetails, 'primePayers');
        }

        this.clearPrimePayersFileInput();

        this.$bvToast.toast('Imported prime payers deleted.', {
          title: 'Success', variant: 'success', solid: true, autoHideDelay: 3000
        });
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.message || 'Failed to delete imported prime payers. Please try again.',
          { title: 'Error', variant: 'danger', solid: true, autoHideDelay: 5000 }
        );
      } finally {
        this.primePayersDeleting = false;
      }
    },

    async generateCreditAssessmentReport() {
      if (!this.canAnalyseModelResult) return;

      this.generatingCreditAssessmentReport = true;
      this.assessmentError = null;
      this.assessmentProgress = {
        status: 'processing',
        message: 'Connecting...',
        progress: 0,
        step: null,
        error: null
      };

      this.disconnectAssessmentWebSocket();

      try {
        await this.connectAssessmentWebSocketAndStartJob();
      } catch (error) {
        this.handleAssessmentStartError(error);
      }
    },

    connectAssessmentWebSocketAndStartJob() {
      return new Promise((resolve, reject) => {
        const wsUrl = `${process.env.VUE_APP_SOCKET_URL}/automated-assessment/${this.candidateId}/`;
        this.assessmentWebSocketReady = false;
        this.assessmentWebsocket = new WebSocket(wsUrl);

        const connectionTimeout = setTimeout(() => {
          reject(new Error('WebSocket connection timed out. Please try again.'));
        }, 15000);

        this.assessmentWebsocket.onopen = async () => {
          clearTimeout(connectionTimeout);

          try {
            this.assessmentProgress = {
              status: 'processing',
              message: 'Starting automated assessment analysis...',
              progress: 0,
              step: null,
              error: null
            };

            await ChatbotService.analyzeAutomatedAssessment(this.candidateId, {
              file_id: this.creditModelFileId
            });

            this.assessmentWebSocketReady = true;
            resolve();
          } catch (error) {
            reject(error);
          }
        };

        this.assessmentWebsocket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleAssessmentWebSocketMessage(data);
          } catch (error) {
            console.error('Error parsing automated assessment WebSocket message:', error);
          }
        };

        this.assessmentWebsocket.onerror = () => {
          clearTimeout(connectionTimeout);
          if (!this.assessmentWebSocketReady) {
            reject(new Error('WebSocket connection failed. Please try again.'));
            return;
          }
          if (this.generatingCreditAssessmentReport) {
            this.handleAssessmentConnectionLost();
          }
        };

        this.assessmentWebsocket.onclose = () => {
          if (this.generatingCreditAssessmentReport && this.assessmentWebSocketReady) {
            this.handleAssessmentConnectionLost();
          }
        };
      });
    },

    handleAssessmentWebSocketMessage(data) {
      if (data.file_id && this.creditModelFileId && data.file_id !== this.creditModelFileId) return;

      const status = data.status || data.type;

      if (status === 'processing') {
        this.assessmentProgress = {
          status: 'processing',
          message: data.message || this.getAssessmentStepLabel(data.step),
          progress: data.progress ?? this.assessmentProgress?.progress ?? 0,
          step: data.step || null,
          error: null
        };
        return;
      }

      if (status === 'completed') {
        this.assessmentProgress = {
          status: 'completed',
          message: data.message || 'Automated assessment analysis completed successfully',
          progress: 100,
          step: 'completed',
          error: null
        };
        this.completeAssessmentRun();
        return;
      }

      if (status === 'failed') {
        const errorMessage = data.error || data.message || 'Automated assessment analysis failed.';
        this.assessmentProgress = {
          status: 'failed',
          message: data.message || errorMessage,
          progress: data.progress ?? 0,
          step: data.step || null,
          error: errorMessage
        };
        this.failAssessmentRun(errorMessage);
      }
    },

    completeAssessmentRun() {
      this.generatingCreditAssessmentReport = false;
      this.assessmentWebSocketReady = false;
      this.assessmentError = null;
      this.creditModelHasAnalysisData = true;
      this.disconnectAssessmentWebSocket();

      this.$bvToast.toast('Credit assessment analysis completed successfully.', {
        title: 'Success', variant: 'success', solid: true, autoHideDelay: 3000
      });

      this.goToCreditModelReport();
    },

    goToCreditModelReport() {
      if (!this.creditModelFileId) return;

      this.$router.push({
        name: 'chatbot-report-details',
        params: { candidateId: this.candidateId, fileId: this.creditModelFileId },
        query: { reportType: 'credit_model' }
      });
    },

    goToPayerReport() {
      this.$router.push({
        name: 'chatbot-payer-report-details',
        params: { candidateId: this.candidateId }
      });
    },

    failAssessmentRun(errorMessage) {
      this.generatingCreditAssessmentReport = false;
      this.assessmentWebSocketReady = false;
      this.assessmentError = errorMessage;
      this.disconnectAssessmentWebSocket();

      this.$bvToast.toast(errorMessage, {
        title: 'Analysis failed', variant: 'danger', solid: true, autoHideDelay: 6000
      });
    },

    handleAssessmentStartError(error) {
      console.error('Error starting credit assessment analysis:', error);
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to start credit assessment analysis. Please try again.';

      this.generatingCreditAssessmentReport = false;
      this.assessmentWebSocketReady = false;
      this.assessmentError = errorMessage;
      this.assessmentProgress = {
        status: 'failed', message: errorMessage, progress: 0, step: null, error: errorMessage
      };
      this.disconnectAssessmentWebSocket();

      this.$bvToast.toast(errorMessage, {
        title: 'Error', variant: 'danger', solid: true, autoHideDelay: 5000
      });
    },

    handleAssessmentConnectionLost() {
      if (!this.generatingCreditAssessmentReport) return;

      const errorMessage = 'Connection lost while analysis was in progress. Please retry.';
      this.failAssessmentRun(errorMessage);
      this.assessmentProgress = {
        status: 'failed',
        message: errorMessage,
        progress: this.assessmentProgress?.progress ?? 0,
        step: this.assessmentProgress?.step || null,
        error: errorMessage
      };
    },

    disconnectAssessmentWebSocket() {
      if (!this.assessmentWebsocket) return;

      this.assessmentWebsocket.onopen = null;
      this.assessmentWebsocket.onmessage = null;
      this.assessmentWebsocket.onerror = null;
      this.assessmentWebsocket.onclose = null;

      if (this.assessmentWebsocket.readyState === WebSocket.OPEN
        || this.assessmentWebsocket.readyState === WebSocket.CONNECTING) {
        this.assessmentWebsocket.close();
      }

      this.assessmentWebsocket = null;
      this.assessmentWebSocketReady = false;
    },

    getAssessmentStepLabel(step) {
      if (!step) return 'Starting automated assessment analysis...';
      return ASSESSMENT_STEP_LABELS[step] || 'Processing analysis...';
    },

    getAssessmentStatusIcon(status) {
      if (status === 'completed') return 'las la-check-circle';
      if (status === 'failed') return 'las la-times-circle';
      return 'las la-spinner la-spin';
    }
  }
};
</script>

<style lang="scss" scoped>
@import './index.scss';

.automated-assessment-report-page {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  min-height: 100%;
}

.report-page-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eef1f6;
}

.assessment-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #101828;
  font-family: $brand-font-bold;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
  color: #4a5568;
  gap: 0.75rem;
}

.empty-state-icon {
  font-size: 3rem;
  color: #cbd5e0;
}

.empty-state-hint {
  margin: 0;
  font-size: 0.875rem;
  color: #718096;
}

.error-state p {
  color: #e53e3e;
  font-size: 1rem;
  margin: 0 0 1rem;
}

.retry-btn {
  background: $color-primary;
  color: $color-secondary;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
}

.report-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.subsection-title {
  margin: 0 0 0.65rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #101828;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-family: $brand-font-bold;

  &::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 0.9rem;
    border-radius: 999px;
    background: linear-gradient(180deg, #0f9d58, #16a34a);
    flex-shrink: 0;
  }
}

/* ── Approved credit fields ── */
.approved-credit-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
  gap: 1rem;
  align-items: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.approved-credit-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: #667085;
  font-family: $brand-font-semi-bold;
}

.rate-display-card {
  width: 100%;
  min-height: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  padding: 0.625rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-sizing: border-box;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25;
  color: #2d3748;
  background: #f8fafc;
  font-family: $brand-font-regular;
  overflow: hidden;
}

.rate-display-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: #2d3748;
}

.rate-display-placeholder {
  font-size: 0.875rem;
  color: #a0aec0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.form-input {
  width: 100%;
  min-height: 38px;
  height: 38px;
  padding: 0.625rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #2d3748;
  background: #ffffff;
  font-family: $brand-font-regular;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 2px rgba($color-primary, 0.15);
  }
}

/* ── Prime payers import ── */
.prime-payers-import {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.prime-payers-import-hint {
  margin: 0;
  font-size: 0.8125rem;
  color: #718096;
  font-family: $brand-font-regular;
  line-height: 1.5;
}

.prime-payers-import-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.prime-payers-file-input-container {
  position: relative;
  flex: 1;
  min-width: 240px;
  max-width: 400px;
  height: 42px;
  border: 1px dashed #cbd5e0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #f8fafc;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: $color-primary;
    background: rgba($color-primary, 0.04);
  }
}

.prime-payers-file-input {
  position: absolute;
  inset: 0;
  z-index: 1;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.prime-payers-file-input-overlay {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.875rem;
  pointer-events: none;
  overflow: hidden;
  max-width: 100%;
}

.prime-payers-file-icon {
  flex-shrink: 0;
  font-size: 1.125rem;
  color: $color-primary;
}

.prime-payers-file-hint {
  font-size: 0.8125rem;
  color: #a0aec0;
  font-family: $brand-font-regular;
  white-space: nowrap;
}

.prime-payers-file-selected {
  font-size: 0.8125rem;
  color: #2d3748;
  font-family: $brand-font-medium;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prime-payers-check-error {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 0.875rem;
  border-radius: 8px;
  border: 1px solid #feb2b2;
  background: #fff5f5;
  color: #c53030;
  font-size: 0.875rem;
  line-height: 1.4;

  i { flex-shrink: 0; margin-top: 0.1rem; }
}

.prime-payers-check-loading {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.875rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #4a5568;
  font-size: 0.875rem;
}

/* ── Resolution panel ── */
.prc-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
}

.prc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8fafc;
  border-radius: 10px 10px 0 0;
}

.prc-summary-text {
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0;
  line-height: 1.4;

  strong { color: #1e293b; }
}

/* Filter tabs */
.prc-filter-tabs {
  display: flex;
  gap: 0.25rem;
}

.prc-tab {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  border-radius: 6px;
  padding: 0.25rem 0.625rem;
  font-size: 0.8rem;
  font-family: $brand-font-medium;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover { border-color: #cbd5e0; color: #1a202c; }

  &.active {
    background: #101828;
    border-color: #101828;
    color: #ffffff;
  }
}

/* List rows (CSS grid columns: name | status | resolution) */
.prc-list {
  display: flex;
  flex-direction: column;
}

.prc-row {
  display: grid;
  grid-template-columns: 1fr 90px 260px;
  align-items: center;
  gap: 1rem;
  padding: 0.625rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.1s;

  &:last-child { border-bottom: none; }
  &:not(.prc-row--head):hover { background: #fafbfd; }

  &--head {
    background: #f8fafc;
    border-bottom: 1px solid #e9ecef;

    span {
      font-size: 0.6875rem;
      font-weight: 600;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
  }
}

.prc-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  font-family: $brand-font-semi-bold;
  line-height: 1.3;
}

.prc-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 0.65rem;
  margin-top: 0.2rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.prc-status-badge {
  display: inline-block;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  white-space: nowrap;

  &.matched { background: #dcfce7; color: #15803d; }
  &.new      { background: #f1f5f9; color: #475569; }
}

.prc-new-text {
  font-size: 0.8125rem;
  color: #94a3b8;
  font-style: italic;
}

.prc-empty-cell {
  text-align: center;
  padding: 2rem 1rem;
  color: #94a3b8;
  font-size: 0.875rem;
  grid-column: 1 / -1;
}

/* Confirm action bar */
.prc-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  border-top: 1px solid #e9ecef;
  background: #f8fafc;
  border-radius: 0 0 10px 10px;
}

.prc-actions-note {
  font-size: 0.8125rem;
  color: #64748b;
}

/* ── Imported prime payers table ── */
.prime-payers-section { /* spacing handled by gap on report-content */ }

.prime-payers-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 1rem;

  .subsection-title { margin-bottom: 0; }
}

.prime-payers-table-wrapper { overflow-x: auto; }

.prime-payers-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e2e8f0;

  thead {
    background: #f8f9fa;
    border-bottom: 1px solid #e2e8f0;

    th {
      padding: 0.625rem 0.875rem;
      text-align: left;
      font-size: 0.6875rem;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }
  }

  tbody td {
    padding: 0.625rem 0.875rem;
    font-size: 0.8125rem;
    color: #2d3748;
    border-bottom: 1px solid #f1f3f5;
  }

  tbody tr:last-child td { border-bottom: none; }
}

/* ── Assessment progress ── */
.assessment-progress-section {
  padding: 1rem;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.assessment-progress-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.assessment-progress-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  font-family: $brand-font-medium;

  &.processing { color: #3b82f6; }
  &.completed  { color: #16a34a; }
  &.failed     { color: #dc2626; }
}

.assessment-progress-message {
  color: #4a5568;
  font-size: 0.875rem;
  line-height: 1.5;
}

.assessment-progress-bar-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.assessment-progress-bar {
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.assessment-progress-bar-fill {
  height: 100%;
  background: $color-primary;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.assessment-progress-percentage {
  min-width: 2.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #4a5568;
  text-align: right;
}

.assessment-progress-error {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: #dc2626;
  font-size: 0.875rem;
  line-height: 1.5;
}

/* ── Actions ── */
.credit-assessment-report-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-start;
  align-items: center;
  padding-top: 1.25rem;
  border-top: 1px solid #e2e8f0;
}

.analyse-prereq-hint {
  flex: 1 1 100%;
  margin: 0;
  font-size: 0.85rem;
  color: #b91c1c;
}
</style>
